import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, FlowingWaves } from '@/components/feature/DrawingLines';

// ── Data ──────────────────────────────────────────────────────────────────────

const REGIONS = ['전체', '서울', '경기/인천', '부산/경남', '대구/경북', '대전/충청', '광주/전라', '강원/제주'];

interface Store {
  id: number;
  name: string;
  region: string;
  district: string;
  address: string;
  phone: string;
  hours: string;
  openDate: string;
  type: 'Type A' | 'Type B' | 'Type C';
  area: string;
  artists: number;
  tags: string[];
  image: string;
  mapUrl: string;
  lat: number;
  lng: number;
  featured?: boolean;
}

const stores: Store[] = [
  {
    id: 1,
    name: '선율 청담 플래그십',
    region: '서울',
    district: '강남구 청담',
    address: '서울 강남구 청담동 123-4 청담빌딩 2F',
    phone: '02-1234-5678',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2023.03',
    type: 'Type B',
    area: '15평',
    artists: 4,
    tags: ['플래그십', '프리미엄', '주차가능'],
    image: 'https://public.readdy.ai/ai/img_res/4fc1ca9aa514608cee6b1a511c6f50a6.jpg',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3165.4!2d127.0495!3d37.5245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDMxJzI4LjIiTiAxMjfCsDAyJzU4LjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.5245,
    lng: 127.0495,
    featured: true,
  },
  {
    id: 2,
    name: '선율 홍대점',
    region: '서울',
    district: '마포구 홍대',
    address: '서울 마포구 와우산로 94 홍대입구역 3번 출구 도보 3분',
    phone: '02-2345-6789',
    hours: '매일 12:00 – 22:00',
    openDate: '2023.08',
    type: 'Type A',
    area: '8평',
    artists: 2,
    tags: ['홍대입구역', '1인샵', '야간운영'],
    image: 'https://readdy.ai/api/search-image?query=cozy%20minimalist%20tattoo%20studio%20interior%20Hongdae%20Seoul%20warm%20lighting%20white%20walls%20small%20elegant%20space%20modern%20design%20clean%20aesthetic%20professional%20photography&width=700&height=480&seq=store_card2&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3162.5!2d126.9235!3d37.5563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDMzJzIyLjciTiAxMjbCsDU1JzI0LjYiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.5563,
    lng: 126.9235,
  },
  {
    id: 3,
    name: '선율 강남역점',
    region: '서울',
    district: '서초구 강남역',
    address: '서울 서초구 강남대로 396 강남역 11번 출구 도보 2분',
    phone: '02-3456-7890',
    hours: '평일 10:00 – 21:00 / 주말 11:00 – 20:00',
    openDate: '2024.09',
    type: 'Type B',
    area: '11평',
    artists: 3,
    tags: ['강남역', '주차가능', '예약필수'],
    image: 'https://readdy.ai/api/search-image?query=high%20end%20tattoo%20studio%20interior%20Gangnam%20Seoul%20premium%20location%20sophisticated%20design%20marble%20accents%20warm%20ambient%20lighting%20professional%20photography%20wide%20angle&width=700&height=480&seq=store_card3&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3165.7!2d127.0276!3d37.4979!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDI5JzUyLjQiTiAxMjfCsDAyJzM5LjQiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.4979,
    lng: 127.0276,
  },
  {
    id: 4,
    name: '선율 이태원점',
    region: '서울',
    district: '용산구 이태원',
    address: '서울 용산구 이태원로 177 이태원역 1번 출구 도보 5분',
    phone: '02-4567-8901',
    hours: '매일 13:00 – 23:00',
    openDate: '2024.03',
    type: 'Type B',
    area: '13평',
    artists: 3,
    tags: ['이태원역', '외국인환영', '야간운영'],
    image: 'https://readdy.ai/api/search-image?query=trendy%20tattoo%20studio%20interior%20Itaewon%20Seoul%20multicultural%20artistic%20atmosphere%20dark%20elegant%20design%20professional%20workspace%20clean%20minimal%20aesthetic%20photography&width=700&height=480&seq=store_card4&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3163.5!2d126.9944!3d37.5344!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDMyJzAzLjgiTiAxMjbCsDU5JzM5LjgiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.5344,
    lng: 126.9944,
  },
  {
    id: 5,
    name: '선율 성수점',
    region: '서울',
    district: '성동구 성수',
    address: '서울 성동구 성수이로 78 성수역 2번 출구 도보 7분',
    phone: '02-5678-9012',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2025.01',
    type: 'Type C',
    area: '10평',
    artists: 2,
    tags: ['성수역', '신규오픈', '트렌디'],
    image: 'https://readdy.ai/api/search-image?query=modern%20industrial%20tattoo%20studio%20interior%20Seongsu%20Seoul%20brick%20wall%20exposed%20concrete%20artistic%20gallery%20vibe%20warm%20lighting%20clean%20design%20professional%20photography&width=700&height=480&seq=store_card5&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3163.0!2d127.0567!3d37.5445!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDMyJzQwLjIiTiAxMjfCsDAzJzI0LjEiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.5445,
    lng: 127.0567,
  },
  {
    id: 6,
    name: '선율 분당점',
    region: '경기/인천',
    district: '성남시 분당',
    address: '경기 성남시 분당구 정자일로 95 정자역 1번 출구 도보 3분',
    phone: '031-1234-5678',
    hours: '평일 10:00 – 20:00 / 주말 11:00 – 19:00',
    openDate: '2023.11',
    type: 'Type B',
    area: '12평',
    artists: 3,
    tags: ['정자역', '주차가능', '가족친화'],
    image: 'https://readdy.ai/api/search-image?query=elegant%20tattoo%20studio%20interior%20Bundang%20Seongnam%20Korea%20suburban%20premium%20location%20bright%20clean%20white%20design%20warm%20lighting%20professional%20photography&width=700&height=480&seq=store_card6&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3172.0!2d127.1115!3d37.3595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDIxJzM0LjIiTiAxMjfCsDA2JzQxLjQiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.3595,
    lng: 127.1115,
  },
  {
    id: 7,
    name: '선율 인천 송도점',
    region: '경기/인천',
    district: '인천 연수구 송도',
    address: '인천 연수구 컨벤시아대로 165 센트럴파크역 도보 5분',
    phone: '032-2345-6789',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2024.11',
    type: 'Type A',
    area: '9평',
    artists: 2,
    tags: ['센트럴파크역', '신도시', '주차가능'],
    image: 'https://readdy.ai/api/search-image?query=elegant%20tattoo%20studio%20interior%20Songdo%20Incheon%20modern%20city%20view%20minimalist%20white%20interior%20cozy%20atmosphere%20professional%20lighting%20clean%20design%20photography&width=700&height=480&seq=store_card7&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3175.0!2d126.6295!3d37.3895!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDIzJzIyLjIiTiAxMjbCsDM3JzQ2LjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.3895,
    lng: 126.6295,
  },
  {
    id: 8,
    name: '선율 수원점',
    region: '경기/인천',
    district: '수원시 영통',
    address: '경기 수원시 영통구 영통로 234 영통역 3번 출구 도보 4분',
    phone: '031-3456-7890',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2025.02',
    type: 'Type A',
    area: '8평',
    artists: 2,
    tags: ['영통역', '신규오픈', '주차가능'],
    image: 'https://readdy.ai/api/search-image?query=modern%20minimalist%20tattoo%20studio%20interior%20Suwon%20Korea%20clean%20white%20walls%20warm%20lighting%20small%20cozy%20space%20professional%20design%20photography&width=700&height=480&seq=store_card8&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3175.5!2d127.0445!3d37.2445!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDIxJzIwLjIiTiAxMjfCsDAyJzQwLjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 37.2445,
    lng: 127.0445,
  },
  {
    id: 9,
    name: '선율 부산 해운대점',
    region: '부산/경남',
    district: '부산 해운대구',
    address: '부산 해운대구 해운대해변로 264 해운대역 5번 출구 도보 8분',
    phone: '051-1234-5678',
    hours: '매일 11:00 – 22:00',
    openDate: '2024.01',
    type: 'Type B',
    area: '12평',
    artists: 3,
    tags: ['해운대역', '관광지', '야간운영'],
    image: 'https://readdy.ai/api/search-image?query=premium%20tattoo%20studio%20interior%20Haeundae%20Busan%20Korea%20ocean%20view%20modern%20design%20white%20gold%20accents%20spacious%20consultation%20area%20elegant%20lighting%20professional%20photography&width=700&height=480&seq=store_card9&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3258.0!2d129.1595!3d35.1595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDA5JzM0LjIiTiAxMjnCsDA5JzM0LjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 35.1595,
    lng: 129.1595,
    featured: true,
  },
  {
    id: 10,
    name: '선율 부산 서면점',
    region: '부산/경남',
    district: '부산 부산진구 서면',
    address: '부산 부산진구 서면로 68 서면역 7번 출구 도보 3분',
    phone: '051-2345-6789',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2024.07',
    type: 'Type A',
    area: '9평',
    artists: 2,
    tags: ['서면역', '번화가', '예약필수'],
    image: 'https://readdy.ai/api/search-image?query=stylish%20tattoo%20studio%20interior%20Seomyeon%20Busan%20Korea%20downtown%20location%20sleek%20black%20design%20artistic%20atmosphere%20professional%20workspace%20clean%20minimal%20photography&width=700&height=480&seq=store_card10&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3258.5!2d129.0595!3d35.1545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDA5JzE2LjIiTiAxMjnCsDAzJzM0LjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 35.1545,
    lng: 129.0595,
  },
  {
    id: 11,
    name: '선율 대구 동성로점',
    region: '대구/경북',
    district: '대구 중구 동성로',
    address: '대구 중구 동성로 2가 123 반월당역 15번 출구 도보 5분',
    phone: '053-1234-5678',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2024.05',
    type: 'Type C',
    area: '10평',
    artists: 2,
    tags: ['반월당역', '번화가', '주차가능'],
    image: 'https://readdy.ai/api/search-image?query=modern%20tattoo%20studio%20interior%20Dongseongro%20Daegu%20Korea%20downtown%20location%20sleek%20black%20design%20artistic%20atmosphere%20professional%20workspace%20clean%20minimal%20aesthetic%20photography&width=700&height=480&seq=store_card11&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3234.0!2d128.5945!3d35.8695!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDUyJzEwLjIiTiAxMjjCsDM1JzQwLjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 35.8695,
    lng: 128.5945,
  },
  {
    id: 12,
    name: '선율 대전 둔산점',
    region: '대전/충청',
    district: '대전 서구 둔산',
    address: '대전 서구 둔산로 123 갤러리아타임월드 인근',
    phone: '042-1234-5678',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2024.09',
    type: 'Type B',
    area: '11평',
    artists: 3,
    tags: ['둔산동', '주차가능', '프리미엄'],
    image: 'https://readdy.ai/api/search-image?query=elegant%20tattoo%20studio%20interior%20Dunsan%20Daejeon%20Korea%20modern%20clean%20white%20design%20warm%20lighting%20professional%20space%20gallery%20aesthetic%20photography&width=700&height=480&seq=store_card12&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3215.0!2d127.3845!3d36.3545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDIxJzE2LjIiTiAxMjfCsDIzJzA0LjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 36.3545,
    lng: 127.3845,
  },
  {
    id: 13,
    name: '선율 광주 충장로점',
    region: '광주/전라',
    district: '광주 동구 충장로',
    address: '광주 동구 충장로 5가 123 충장로역 3번 출구 도보 4분',
    phone: '062-1234-5678',
    hours: '평일 11:00 – 21:00 / 주말 12:00 – 20:00',
    openDate: '2025.03',
    type: 'Type A',
    area: '9평',
    artists: 2,
    tags: ['충장로역', '신규오픈', '번화가'],
    image: 'https://readdy.ai/api/search-image?query=modern%20tattoo%20studio%20interior%20Chungjangno%20Gwangju%20Korea%20clean%20minimalist%20design%20warm%20lighting%20professional%20space%20artistic%20atmosphere%20photography&width=700&height=480&seq=store_card13&orientation=landscape',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3248.0!2d126.9145!3d35.1495!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDA4JzU4LjIiTiAxMjbCsDU0JzUyLjIiRQ!5e0!3m2!1sko!2skr!4v1',
    lat: 35.1495,
    lng: 126.9145,
  },
];

const typeColors: Record<string, string> = {
  'Type A': '#888888',
  'Type B': '#C9A96E',
  'Type C': '#1A1A1A',
};

const typeLabels: Record<string, string> = {
  'Type A': 'Type A 실속형',
  'Type B': 'Type B 프리미엄',
  'Type C': 'Type C 자율시공',
};

// ── Hero ──────────────────────────────────────────────────────────────────────

function StoresHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 100); return () => clearTimeout(t); }, []);

  return (
    <section className="relative flex items-center overflow-hidden bg-[#1A1A1A]" style={{ minHeight: '580px' }}>
      <img
        src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Korean%20city%20at%20night%20with%20glowing%20lights%20urban%20landscape%20Seoul%20Korea%20beautiful%20cityscape%20premium%20photography%20wide%20angle%20cinematic%20dark%20moody%20atmosphere&width=1440&height=600&seq=stores_hero_v1&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.3) 40%, rgba(10,10,10,0.85) 100%)' }} />
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#C9A96E]" />
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.15)" size={160} />

      <div
        className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-16 pt-36 pb-20 md:pt-40 md:pb-24 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
        }}
      >
        <div className="flex items-center justify-center gap-2 mb-10">
          <Link to="/franchise" className="text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-white/60 transition-colors">
            Franchise
          </Link>
          <span className="text-white/20">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]">가맹점 찾기</span>
        </div>

        <h1
          className="text-[32px] md:text-[54px] text-white mb-6 leading-snug break-keep"
          style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.06em' }}
        >
          가맹점 찾기<br />
          <span style={{ color: 'rgba(255,255,255,0.35)' }}>Find a Studio Near You</span>
        </h1>

        <p
          className="text-sm md:text-[15px] leading-relaxed break-keep max-w-[320px] md:max-w-none mx-auto"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          전국 47개 선율 가맹점에서 동일한 프리미엄 경험을 만나보세요.
        </p>

        <div className="w-14 h-px mx-auto my-10" style={{ background: 'rgba(201,169,110,0.5)' }} />

        <div className="flex flex-wrap items-center justify-center gap-y-8 gap-x-10 md:gap-x-16">
          {[
            { value: '47+', label: '전국 가맹점' },
            { value: '13개', label: '광역시·도 운영' },
            { value: '97%', label: '고객 만족도' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-3xl md:text-[32px] text-white font-bold mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.01em', lineHeight: 1.1 }}
              >
                {stat.value}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Search & Filter ───────────────────────────────────────────────────────────

interface FilterBarProps {
  selectedRegion: string;
  onRegionChange: (r: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  resultCount: number;
}

function FilterBar({ selectedRegion, onRegionChange, searchQuery, onSearchChange, resultCount }: FilterBarProps) {
  return (
    <section className="sticky top-16 md:top-20 z-30 bg-white border-b border-[#EBEBEB]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 py-4 md:py-5">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          {/* 검색창 */}
          <div className="relative flex-shrink-0 w-full md:w-72">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#AAAAAA] text-sm" />
            <input
              type="text"
              placeholder="지역명, 지점명으로 검색"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-[#EBEBEB] bg-[#FAFAFA] text-[#1A1A1A] placeholder-[#AAAAAA] focus:outline-none focus:border-[#C9A96E] transition-colors duration-200"
              style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
            />
          </div>

          {/* 지역 필터 */}
          <div className="flex flex-wrap gap-2 flex-1">
            {REGIONS.map((r) => (
              <button
                key={r}
                onClick={() => onRegionChange(r)}
                className={`px-4 py-2 text-[11px] tracking-[0.1em] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedRegion === r
                    ? 'bg-[#1A1A1A] text-white'
                    : 'bg-[#F5F5F5] text-[#888] hover:bg-[#EBEBEB] hover:text-[#1A1A1A]'
                }`}
                style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
              >
                {r}
              </button>
            ))}
          </div>

          {/* 결과 수 */}
          <div className="flex-shrink-0 text-[11px] text-[#AAAAAA] whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <span className="text-[#1A1A1A] font-semibold">{resultCount}</span>개 지점
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Store Card ────────────────────────────────────────────────────────────────

interface StoreCardProps {
  store: Store;
  index: number;
  visible: boolean;
  isSelected: boolean;
  onSelect: (id: number) => void;
}

function StoreCard({ store, index, visible, isSelected, onSelect }: StoreCardProps) {
  return (
    <div
      className={`group cursor-pointer border transition-all duration-300 ${
        isSelected ? 'border-[#C9A96E]' : 'border-[#EBEBEB] hover:border-[#C9A96E]'
      }`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.3s',
        transitionDelay: `${index * 60 + 100}ms`,
      }}
      onClick={() => onSelect(store.id)}
    >
      {/* 이미지 */}
      <div className="relative overflow-hidden" style={{ height: '200px' }}>
        <img
          src={store.image}
          alt={store.name}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent" />

        {/* 타입 배지 */}
        <div className="absolute top-3 left-3">
          <span
            className="text-[9px] px-2.5 py-1 font-semibold tracking-wide text-white"
            style={{ background: typeColors[store.type], fontFamily: "'Montserrat', sans-serif" }}
          >
            {typeLabels[store.type]}
          </span>
        </div>

        {/* 신규 배지 */}
        {store.tags.includes('신규오픈') && (
          <div className="absolute top-3 right-3">
            <span className="text-[9px] px-2.5 py-1 bg-[#C9A96E] text-white font-semibold tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              NEW
            </span>
          </div>
        )}

        {/* 지역 */}
        <div className="absolute bottom-3 left-3">
          <span className="text-[10px] text-white/80 flex items-center gap-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <i className="ri-map-pin-line text-[#C9A96E]" />
            {store.district}
          </span>
        </div>
      </div>

      {/* 정보 */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <h3
            className="text-base text-[#1A1A1A] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
          >
            {store.name}
          </h3>
          <span className="text-[10px] text-[#AAAAAA] flex-shrink-0 ml-2 mt-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {store.openDate}
          </span>
        </div>

        <p className="text-xs text-[#888] mb-3 leading-relaxed line-clamp-1">{store.address}</p>

        <div className="flex items-center gap-4 mb-4 py-3 border-y border-[#F5F5F5]">
          <div className="flex items-center gap-1.5">
            <i className="ri-ruler-line text-[#C9A96E] text-xs" />
            <span className="text-xs text-[#555]">{store.area}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-user-line text-[#C9A96E] text-xs" />
            <span className="text-xs text-[#555]">아티스트 {store.artists}명</span>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-phone-line text-[#C9A96E] text-xs" />
            <span className="text-xs text-[#555]">{store.phone}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {store.tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-2.5 py-1 bg-[#F5F5F5] text-[#888] whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
            >
              #{tag}
            </span>
          ))}
        </div>

        <button
          className="w-full py-2.5 text-[10px] tracking-[0.15em] uppercase font-medium border border-[#EBEBEB] text-[#888] hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all duration-200 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          onClick={(e) => { e.stopPropagation(); onSelect(store.id); }}
        >
          상세 정보 보기
        </button>
      </div>
    </div>
  );
}

// ── Store Detail Modal ────────────────────────────────────────────────────────

interface StoreDetailProps {
  store: Store | null;
  onClose: () => void;
}

function StoreDetailModal({ store, onClose }: StoreDetailProps) {
  useEffect(() => {
    if (store) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [store]);

  if (!store) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 이미지 헤더 */}
        <div className="relative" style={{ height: '280px' }}>
          <img src={store.image} alt={store.name} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <div className="absolute bottom-5 left-6">
            <span
              className="text-[9px] px-2.5 py-1 font-semibold tracking-wide text-white mb-2 inline-block"
              style={{ background: typeColors[store.type], fontFamily: "'Montserrat', sans-serif" }}
            >
              {typeLabels[store.type]}
            </span>
            <h2
              className="text-2xl md:text-3xl text-white"
              style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300 }}
            >
              {store.name}
            </h2>
            <p className="text-sm text-white/60 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {store.district} · {store.openDate} 오픈
            </p>
          </div>
        </div>

        {/* 상세 정보 */}
        <div className="p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 기본 정보 */}
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>Store Info</p>
              <ul className="flex flex-col gap-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 border border-[#EBEBEB]">
                    <i className="ri-map-pin-line text-[#C9A96E] text-sm" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>주소</p>
                    <p className="text-sm text-[#1A1A1A] leading-relaxed">{store.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 border border-[#EBEBEB]">
                    <i className="ri-phone-line text-[#C9A96E] text-sm" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>전화번호</p>
                    <p className="text-sm text-[#1A1A1A]">{store.phone}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 border border-[#EBEBEB]">
                    <i className="ri-time-line text-[#C9A96E] text-sm" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>운영시간</p>
                    <p className="text-sm text-[#1A1A1A] leading-relaxed">{store.hours}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 border border-[#EBEBEB]">
                    <i className="ri-ruler-line text-[#C9A96E] text-sm" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>규모</p>
                    <p className="text-sm text-[#1A1A1A]">{store.area} · 아티스트 {store.artists}명</p>
                  </div>
                </li>
              </ul>

              <div className="flex flex-wrap gap-1.5 mt-5">
                {store.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] px-2.5 py-1 bg-[#F5F5F5] text-[#888]"
                    style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* 지도 */}
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>Location</p>
              <div className="w-full border border-[#EBEBEB] overflow-hidden" style={{ height: '240px' }}>
                <iframe
                  src={store.mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${store.name} 위치`}
                />
              </div>
              <p className="text-[11px] text-[#AAAAAA] mt-2">※ 정확한 위치는 전화 문의 후 방문해주세요.</p>
            </div>
          </div>

          {/* CTA 버튼 */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-[#F0F0F0]">
            <Link
              to="/community/inquiry"
              className="flex-1 py-3.5 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium text-center hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              이 지점 예약 문의
            </Link>
            <a
              href={`tel:${store.phone}`}
              className="flex-1 py-3.5 border border-[#EBEBEB] text-[#555] text-[10px] tracking-[0.2em] uppercase font-medium text-center hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              전화 연결
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Featured Stores ───────────────────────────────────────────────────────────

function FeaturedStores() {
  const { ref, visible } = useScrollAnimation(0.05);
  const featured = stores.filter((s) => s.featured);

  return (
    <section className="py-20 md:py-28 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.07)" lineCount={3} />
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.15)" size={150} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-16">
        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Featured Stores</p>
          <h2
            className="text-2xl md:text-4xl text-white"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            대표 플래그십 스토어
          </h2>
        </div>
        <div className="mb-10">
          <GoldLineDivider delay={0.2} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featured.map((store, i) => (
            <div
              key={store.id}
              className="group relative overflow-hidden cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
                transitionDelay: `${i * 150 + 200}ms`,
                height: '360px',
              }}
            >
              <img
                src={store.image}
                alt={store.name}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/90 via-[#1A1A1A]/30 to-transparent" />
              <div className="absolute inset-0 border border-white/10 group-hover:border-[#C9A96E]/50 transition-colors duration-300" />

              <div className="absolute bottom-0 left-0 right-0 p-7">
                <span
                  className="text-[9px] px-2.5 py-1 font-semibold tracking-wide text-white mb-3 inline-block"
                  style={{ background: typeColors[store.type], fontFamily: "'Montserrat', sans-serif" }}
                >
                  {typeLabels[store.type]}
                </span>
                <h3
                  className="text-xl md:text-2xl text-white mb-1"
                  style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300 }}
                >
                  {store.name}
                </h3>
                <p className="text-xs text-white/50 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {store.district} · {store.area} · 아티스트 {store.artists}명
                </p>
                <div className="flex items-center gap-1.5 text-xs text-white/40">
                  <i className="ri-map-pin-line text-[#C9A96E]" />
                  <span>{store.address}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── All Stores Grid ───────────────────────────────────────────────────────────

function AllStoresSection() {
  const { ref, visible } = useScrollAnimation(0.03);
  const [selectedRegion, setSelectedRegion] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    return stores.filter((s) => {
      const matchRegion = selectedRegion === '전체' || s.region === selectedRegion;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || s.name.toLowerCase().includes(q) || s.district.toLowerCase().includes(q) || s.address.toLowerCase().includes(q);
      return matchRegion && matchSearch;
    });
  }, [selectedRegion, searchQuery]);

  const selectedStore = stores.find((s) => s.id === selectedStoreId) ?? null;

  return (
    <>
      <FilterBar
        selectedRegion={selectedRegion}
        onRegionChange={setSelectedRegion}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        resultCount={filtered.length}
      />

      <section className="py-16 md:py-24 bg-white overflow-hidden relative" ref={ref}>
        <BotanicalCorner position="top-left" color="rgba(201,169,110,0.07)" size={140} />
        <div className="max-w-[1440px] mx-auto px-6 md:px-16">
          <div
            className="text-center mb-10 transition-all duration-1000 ease-out"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
          >
            <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>All Stores</p>
            <h2
              className="text-2xl md:text-4xl text-[#222222]"
              style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
            >
              전국 가맹점 목록
            </h2>
          </div>
          <div className="mb-10">
            <GoldLineDivider delay={0.2} />
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 border border-[#EBEBEB]">
                <i className="ri-map-pin-line text-2xl text-[#AAAAAA]" />
              </div>
              <p className="text-sm text-[#AAAAAA]">검색 결과가 없습니다.</p>
              <button
                onClick={() => { setSelectedRegion('전체'); setSearchQuery(''); }}
                className="mt-4 text-xs text-[#C9A96E] underline cursor-pointer"
              >
                필터 초기화
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((store, i) => (
                <StoreCard
                  key={store.id}
                  store={store}
                  index={i}
                  visible={visible}
                  isSelected={selectedStoreId === store.id}
                  onSelect={setSelectedStoreId}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <StoreDetailModal store={selectedStore} onClose={() => setSelectedStoreId(null)} />
    </>
  );
}

// ── Region Map Section ────────────────────────────────────────────────────────

function RegionMapSection() {
  const { ref, visible } = useScrollAnimation(0.1);

  const regionStats = [
    { region: '서울', count: 5, icon: 'ri-building-4-line' },
    { region: '경기/인천', count: 3, icon: 'ri-map-2-line' },
    { region: '부산/경남', count: 2, icon: 'ri-ship-line' },
    { region: '대구/경북', count: 1, icon: 'ri-landscape-line' },
    { region: '대전/충청', count: 1, icon: 'ri-government-line' },
    { region: '광주/전라', count: 1, icon: 'ri-leaf-line' },
  ];

  return (
    <section className="py-20 md:py-28 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.08)" size={150} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-16">
        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Coverage</p>
          <h2
            className="text-2xl md:text-4xl text-[#222222] mb-5"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            전국 운영 현황
          </h2>
          <p className="text-sm text-[#888] max-w-md mx-auto leading-relaxed">
            서울부터 제주까지, 선율은 전국 어디서나 동일한 프리미엄 경험을 제공합니다.
          </p>
        </div>
        <div className="mb-10">
          <GoldLineDivider delay={0.2} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {regionStats.map((r, i) => (
            <div
              key={r.region}
              className="bg-white border border-[#EBEBEB] p-6 text-center hover:border-[#C9A96E] transition-colors duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.3s',
                transitionDelay: `${i * 80 + 200}ms`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 border border-[#EBEBEB]">
                <i className={`${r.icon} text-[#C9A96E] text-lg`} />
              </div>
              <p className="text-2xl font-bold text-[#1A1A1A] mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>{r.count}</p>
              <p className="text-[11px] text-[#888]" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>{r.region}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────

function StoresCta() {
  const { ref, visible } = useScrollAnimation(0.2);

  return (
    <section className="py-24 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={3} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.18)" size={150} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div
        className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-16 text-center"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}
      >
        <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Open Your Store</p>
        <h2
          className="text-2xl md:text-5xl text-white mb-6 leading-tight"
          style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
        >
          내 지역에 선율을<br />
          <span className="text-white/40">오픈하고 싶다면?</span>
        </h2>
        <p className="text-sm text-white/40 leading-relaxed max-w-md mx-auto mb-10" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
          아직 선율이 없는 지역에 가맹점을 오픈하세요.<br />
          본사가 상권 분석부터 오픈까지 함께합니다.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/community/inquiry"
            className="px-10 py-4 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            가맹 문의하기
          </Link>
          <Link
            to="/franchise/guide"
            className="px-10 py-4 border border-white/20 text-white/60 text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            창업 안내 보기
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FranchiseStoresPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <StoresHero />
        <FeaturedStores />
        <AllStoresSection />
        <RegionMapSection />
        <StoresCta />
      </main>
      <Footer />
    </div>
  );
}
